import { ApiError, api } from "../api";
import { presetFor } from "../avatar/catalog";
import { avatarPartsReady, drawFrame } from "../avatar/composer";
import { AVATAR_FRAME } from "../config";
import type { Session } from "../session";
import { DOSSIER_TEXT_MAX, UFS, type Dossier, type DossierField, type PublicUser, type Team } from "../types";
import { clearFieldErrors, showFieldErrors } from "./AuthOverlay";
import { requireElement } from "./Hud";
import { escapeHtml } from "../moderation/format";
import { isolateKeyboardFromGame } from "./keyboard";

const PREVIEW_SCALE = 2;

type Tab = "dossier" | "avatar" | "password";

export interface ProfileMenuResult {
  session: Session;
  /** true se avatar ou nome publico mudaram — o mundo precisa reconectar pra refletir. */
  changed: boolean;
  /** true quando o jogador clicou em "Sair" — o chamador deve encerrar a sessao. */
  leave: boolean;
  /** true quando pediu para editar o avatar — o chamador abre o customizador. */
  customize: boolean;
  /** true quando pediu para ver o proprio dossie como os outros veem. */
  preview: boolean;
}

/**
 * Meu Dossie: nome publico, time, cargo, nascimento e cidade (cada um pode
 * ser escondido dos outros), abrir o customizador de avatar e trocar a senha.
 * Resolve quando o jogador fecha o painel, com a sessao (possivelmente atualizada).
 *
 * onLiveUpdate da um retorno visual imediato no HUD (o botao de perfil no
 * canto) assim que avatar/nome mudam; o mundo em si (o avatar dos outros
 * jogadores, o nome no chat) so pega a mudanca quando o chamador reconecta,
 * o que acontece ao fechar o menu se algo mudou.
 */
export function showProfileMenu(session: Session, onLiveUpdate: (user: PublicUser) => void): Promise<ProfileMenuResult> {
  return new Promise((resolve) => {
    const root = requireElement<HTMLDivElement>("overlay-root");
    const overlay = document.createElement("div");
    overlay.className = "overlay";
    overlay.innerHTML = template(session.user.username, session.user.emailVerified);
    root.replaceChildren(overlay);

    const $ = <T extends HTMLElement>(selector: string) => overlay.querySelector(selector) as T;

    let user = session.user;
    let changed = false;
    let leave = false;
    let customize = false;
    let viewOwn = false;

    const notice = $<HTMLParagraphElement>(".notice");
    const setNotice = (text: string | null, level: "ok" | "error" | "info" = "info") => {
      if (!text) {
        notice.classList.add("hidden");
        return;
      }
      notice.className = `notice ${level === "info" ? "" : level}`.trim();
      notice.textContent = text;
      notice.classList.remove("hidden");
    };

    const tabButtons = {
      dossier: $<HTMLButtonElement>('[data-tab="dossier"]'),
      avatar: $<HTMLButtonElement>('[data-tab="avatar"]'),
      password: $<HTMLButtonElement>('[data-tab="password"]'),
    };
    const tabPanels = {
      dossier: $<HTMLDivElement>("#tab-dossier"),
      avatar: $<HTMLDivElement>("#tab-avatar"),
      password: $<HTMLDivElement>("#tab-password"),
    };
    const setTab = (tab: Tab) => {
      for (const key of Object.keys(tabButtons) as Tab[]) {
        tabButtons[key].setAttribute("aria-selected", String(key === tab));
        tabPanels[key].classList.toggle("hidden", key !== tab);
      }
      clearFieldErrors(overlay);
      setNotice(null);
    };
    (Object.keys(tabButtons) as Tab[]).forEach((tab) => {
      tabButtons[tab].addEventListener("click", () => setTab(tab));
    });

    const close = () => {
      document.removeEventListener("keydown", onKey);
      root.replaceChildren();
      resolve({ session: { ...session, user }, changed, leave, customize, preview: viewOwn });
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    isolateKeyboardFromGame(overlay);
    $<HTMLButtonElement>("#profile-close").addEventListener("click", close);
    $<HTMLButtonElement>("#profile-leave").addEventListener("click", () => {
      leave = true;
      close();
    });
    $<HTMLButtonElement>("#profile-preview").addEventListener("click", () => {
      viewOwn = true;
      close();
    });

    const applyUpdate = (updated: PublicUser) => {
      user = updated;
      changed = true;
      onLiveUpdate(user);
    };

    // ------------------------------------------------- confirmacao de e-mail
    const verifyBtn = overlay.querySelector<HTMLButtonElement>("#profile-verify");
    verifyBtn?.addEventListener("click", async () => {
      verifyBtn.disabled = true;
      try {
        const result = await api.resendVerification(session.token);
        setNotice(result.message, "ok");
      } catch (error) {
        setNotice(error instanceof Error ? error.message : "Não foi possível reenviar o link", "error");
        verifyBtn.disabled = false;
      }
    });

    // ------------------------------------------------------------- avatar
    // A edicao acontece no customizador (tela propria); aqui so a previa.
    const preview = $<HTMLCanvasElement>("#profile-avatar");
    preview.width = AVATAR_FRAME.width;
    preview.height = AVATAR_FRAME.height;
    preview.style.width = `${AVATAR_FRAME.width * PREVIEW_SCALE}px`;
    preview.style.height = `${AVATAR_FRAME.height * PREVIEW_SCALE}px`;
    if (avatarPartsReady()) drawFrame(preview, user.appearance ?? presetFor(user.skin), 0);

    $<HTMLButtonElement>("#profile-customize").addEventListener("click", () => {
      customize = true;
      close();
    });

    // ------------------------------------------------------------- dossie
    // Nome publico e dossie saem no mesmo formulario, mas sao rotas diferentes:
    // o nome so vai se mudou (ele reconecta o mundo e tem checagem propria).
    const dossierForm = $<HTMLFormElement>("#form-dossier");
    const field = <T extends HTMLInputElement | HTMLSelectElement>(name: string) => dossierForm.elements.namedItem(name) as T;
    const nameInput = field<HTMLInputElement>("displayName");
    nameInput.value = user.displayName;
    const dossierSubmit = dossierForm.querySelector("button.primary") as HTMLButtonElement;
    dossierSubmit.disabled = true;

    const fillDossier = (d: Dossier, teams: Team[]) => {
      const team = field<HTMLSelectElement>("teamId");
      team.innerHTML =
        `<option value="">Sem time</option>` +
        teams.map((t) => `<option value="${t.id}">${escapeHtml(t.name)}</option>`).join("");
      team.value = d.teamId ? String(d.teamId) : "";
      if (!teams.length) team.disabled = true;
      field<HTMLInputElement>("jobTitle").value = d.jobTitle;
      field<HTMLInputElement>("birthDate").value = d.birthDate;
      field<HTMLInputElement>("city").value = d.city;
      field<HTMLSelectElement>("state").value = d.state;
      dossierForm.querySelectorAll<HTMLInputElement>("[data-show]").forEach((box) => {
        box.checked = !d.hidden.includes(box.dataset.show as DossierField);
      });
      dossierSubmit.disabled = false;
    };
    api
      .myDossier(session.token)
      .then(({ dossier, teams }) => fillDossier(dossier, teams))
      .catch((error: unknown) => setNotice(error instanceof Error ? error.message : "Não foi possível carregar o dossiê", "error"));

    dossierForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      clearFieldErrors(overlay);
      setNotice(null);
      dossierSubmit.disabled = true;
      try {
        const name = nameInput.value.trim();
        if (name !== user.displayName) {
          const result = await api.updateDisplayName(session.token, name);
          applyUpdate(result.user);
          nameInput.value = user.displayName;
        }
        const teamId = field<HTMLSelectElement>("teamId").value;
        const hidden = [...dossierForm.querySelectorAll<HTMLInputElement>("[data-show]")]
          .filter((box) => !box.checked)
          .map((box) => box.dataset.show as DossierField);
        const result = await api.updateDossier(session.token, {
          teamId: teamId ? Number(teamId) : null,
          jobTitle: field<HTMLInputElement>("jobTitle").value,
          birthDate: field<HTMLInputElement>("birthDate").value,
          city: field<HTMLInputElement>("city").value,
          state: field<HTMLSelectElement>("state").value,
          hidden,
        });
        field<HTMLInputElement>("jobTitle").value = result.dossier.jobTitle;
        field<HTMLInputElement>("city").value = result.dossier.city;
        setNotice(result.message, "ok");
      } catch (error) {
        if (error instanceof ApiError && Object.keys(error.fields).length > 0) {
          showFieldErrors(overlay, error.fields);
        } else {
          setNotice(error instanceof Error ? error.message : "Não foi possível salvar o dossiê", "error");
        }
      } finally {
        dossierSubmit.disabled = false;
      }
    });

    // -------------------------------------------------------------- senha
    const passwordForm = $<HTMLFormElement>("#form-password");
    passwordForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      clearFieldErrors(overlay);
      const submit = passwordForm.querySelector("button.primary") as HTMLButtonElement;
      const currentPassword = (passwordForm.elements.namedItem("currentPassword") as HTMLInputElement).value;
      const newPassword = (passwordForm.elements.namedItem("newPassword") as HTMLInputElement).value;
      const confirmPassword = (passwordForm.elements.namedItem("confirmPassword") as HTMLInputElement).value;

      if (newPassword !== confirmPassword) {
        showFieldErrors(overlay, { confirmPassword: "As senhas não coincidem" });
        return;
      }

      submit.disabled = true;
      try {
        const result = await api.updatePassword(session.token, currentPassword, newPassword);
        setNotice(result.message, "ok");
        passwordForm.reset();
      } catch (error) {
        if (error instanceof ApiError && Object.keys(error.fields).length > 0) {
          showFieldErrors(overlay, error.fields);
        } else {
          setNotice(error instanceof Error ? error.message : "Não foi possível trocar a senha", "error");
        }
      } finally {
        submit.disabled = false;
      }
    });
  });
}

/** Caixinha "mostrar no dossie" de um campo. */
const showBox = (field: DossierField, label = "Mostrar") =>
  `<label class="dossier-show"><input type="checkbox" data-show="${field}" checked /> ${label}</label>`;

function template(username: string, emailVerified: boolean): string {
  return `
    <div class="panel">
      <div class="profile-header">
        <div>
          <h1>Meu Dossiê</h1>
          <p class="sub">Logado como <strong>${escapeHtml(username)}</strong></p>
          ${
            emailVerified
              ? ""
              : `<p class="sub verify-line">E-mail ainda não confirmado (opcional).
                 <button type="button" id="profile-verify" class="link-btn">reenviar link</button></p>`
          }
        </div>
        <button type="button" id="profile-leave" class="secondary danger small">Sair</button>
      </div>

      <p class="notice hidden"></p>

      <div class="tabs" role="tablist">
        <button type="button" role="tab" data-tab="dossier" aria-selected="true">Dossiê</button>
        <button type="button" role="tab" data-tab="avatar" aria-selected="false">Avatar</button>
        <button type="button" role="tab" data-tab="password" aria-selected="false">Senha</button>
      </div>

      <div id="tab-dossier">
        <form id="form-dossier" novalidate>
          <div class="field">
            <label for="profile-name">Nome público</label>
            <input id="profile-name" name="displayName" type="text" maxlength="20" autocomplete="off" />
          </div>
          <div class="field">
            <div class="dossier-label"><label for="dossier-team">Time</label>${showBox("team")}</div>
            <select id="dossier-team" name="teamId"><option value="">Carregando…</option></select>
          </div>
          <div class="field">
            <div class="dossier-label"><label for="dossier-job">Cargo</label>${showBox("job")}</div>
            <input id="dossier-job" name="jobTitle" type="text" maxlength="${DOSSIER_TEXT_MAX}" autocomplete="organization-title" />
          </div>
          <div class="field">
            <div class="dossier-label">
              <label for="dossier-birth">Data de nascimento</label>
              <span class="dossier-shows">${showBox("age", "Idade")}${showBox("birthday", "Aniversário")}</span>
            </div>
            <input id="dossier-birth" name="birthDate" type="date" autocomplete="bday" />
            <p class="sub-hint">A data completa nunca aparece: os outros veem só a idade e/ou o dia do aniversário.</p>
          </div>
          <div class="field">
            <div class="dossier-label"><label for="dossier-city">Cidade e estado</label>${showBox("location")}</div>
            <div class="dossier-row">
              <div class="dossier-city"><input id="dossier-city" name="city" type="text" maxlength="${DOSSIER_TEXT_MAX}" autocomplete="address-level2" /></div>
              <div class="dossier-uf">
                <select name="state" aria-label="Estado">
                  <option value="">UF</option>
                  ${UFS.map((uf) => `<option value="${uf}">${uf}</option>`).join("")}
                </select>
              </div>
            </div>
          </div>
          <button type="submit" class="primary">Salvar dossiê</button>
          <button type="button" id="profile-preview" class="secondary">Ver como os outros veem</button>
        </form>
      </div>

      <div id="tab-avatar" class="hidden">
        <div class="profile-avatar-preview">
          <canvas id="profile-avatar" aria-label="Seu avatar"></canvas>
          <div>
            <p class="sub" style="margin-bottom:12px">
              Troque cabelo, roupa, cores e o tom de pele do seu avatar.
            </p>
            <button type="button" id="profile-customize" class="primary">Editar avatar</button>
          </div>
        </div>
      </div>

      <div id="tab-password" class="hidden">
        <form id="form-password" novalidate>
          <div class="field">
            <label for="profile-current-password">Senha atual</label>
            <input id="profile-current-password" name="currentPassword" type="password"
                   autocomplete="current-password" required />
          </div>
          <div class="field">
            <label for="profile-new-password">Nova senha (8+ caracteres, com letras e números)</label>
            <input id="profile-new-password" name="newPassword" type="password"
                   autocomplete="new-password" minlength="8" required />
          </div>
          <div class="field">
            <label for="profile-confirm-password">Confirme a nova senha</label>
            <input id="profile-confirm-password" name="confirmPassword" type="password"
                   autocomplete="new-password" minlength="8" required />
          </div>
          <button type="submit" class="primary">Trocar senha</button>
        </form>
      </div>

      <button type="button" id="profile-close" class="secondary" style="margin-top:16px">Fechar</button>
    </div>
  `;
}
